import { generateClient } from 'aws-amplify/api';
import { fetchMultipleHypeRatings } from './hypeRatingClient';

const client = generateClient();
const API_BASE_URL = "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";

export const adminHypeRatingUtils = {
  // Boost HypeRating for content
  async boostHypeRating(contentID, contentType, boostAmount, reason, adminID) {
    try {
      const result = await client.graphql({
        query: `
          mutation AdminBoostHypeRating($input: AdminBoostHypeRatingInput!) {
            adminBoostHypeRating(input: $input) {
              contentID
              contentType
              newHypeRating
              boostAmount
              reason
            }
          }
        `,
        variables: {
          input: {
            contentID,
            contentType,
            boostAmount,
            reason,
            adminID
          }
        }
      });
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }
      
      return result.data.adminBoostHypeRating;
    } catch (error) {
      console.error('Error boosting HypeRating:', error);
      if (error.errors) {
        console.error('GraphQL errors detail:', error.errors);
      }
      throw error;
    }
  },

  // Recalculate HypeRating
  async recalculateHypeRating(contentID, contentType) {
    try {
      const result = await client.graphql({
        query: `
          mutation CalculateHypeRating($contentID: ID!, $contentType: String!) {
            calculateHypeRating(contentID: $contentID, contentType: $contentType) {
              contentID
              contentType
              hypeRating
              hypeScore
            }
          }
        `,
        variables: {
          contentID,
          contentType
        }
      });
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }
      
      return result.data.calculateHypeRating;
    } catch (error) {
      console.error('Error recalculating HypeRating:', error);
      if (error.errors) {
        console.error('GraphQL errors detail:', error.errors);
      }
      throw error;
    }
  },

  // Get trending content with high HypeRatings
  async getTrendingContent(limit = 20) {
    try {
      // Get trending movies from MongoDB
      const movieResponse = await fetch(`${API_BASE_URL}/movies?filter=trending&limit=${limit}&sort_by=hype_rating`);
      const movieData = await movieResponse.json();
      let movies = movieData.movies || [];
      
      // Get trending shows from MongoDB
      const showResponse = await fetch(`${API_BASE_URL}/shows?filter=trending&limit=${limit}&sort_by=hype_rating`);
      const showData = await showResponse.json();
      let shows = showData.shows || [];
      
      // Fetch HypeRating data from DynamoDB
      movies = await fetchMultipleHypeRatings(movies, 'MOVIE', true);
      shows = await fetchMultipleHypeRatings(shows, 'SHOW', true);
      
      return [...movies.map(item => ({ ...item, type: 'MOVIE' })), ...shows.map(item => ({ ...item, type: 'SHOW' }))]
        .sort((a, b) => (b.hypeRating || 0) - (a.hypeRating || 0))
        .slice(0, limit);
    } catch (error) {
      console.error('Error getting trending content:', error);
      throw error;
    }
  },

  // Search content for admin management
  async searchContent(query, limit = 20) {
    try {
      let movies = [];
      let shows = [];
      
      try {
        // Try MongoDB first
        const [movieResponse, showResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/movies/search?query=${encodeURIComponent(query)}&limit=${limit}`),
          fetch(`${API_BASE_URL}/shows/search?query=${encodeURIComponent(query)}&limit=${limit}`)
        ]);
        
        if (movieResponse.ok) {
          const movieData = await movieResponse.json();
          movies = movieData.movies || [];
        }
        
        if (showResponse.ok) {
          const showData = await showResponse.json();
          shows = showData.shows || [];
        }
        
        // Fetch HypeRating data from DynamoDB
        movies = await fetchMultipleHypeRatings(movies, 'MOVIE', true);
        shows = await fetchMultipleHypeRatings(shows, 'SHOW', true);
        
      } catch (mongoError) {
        console.warn('MongoDB search failed, using DynamoDB fallback');
        // Fallback implementation would go here
      }
      
      return [
        ...movies.map(item => ({ ...item, type: 'MOVIE' })),
        ...shows.map(item => ({ ...item, type: 'SHOW' }))
      ];
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  },

  // Bulk recalculate HypeRating for all movies and shows
  async bulkRecalculateHypeRating() {
    try {
      // Get all movies and shows from DynamoDB
      const [movieStats, showStats] = await Promise.all([
        client.graphql({
          query: `
            query ListMovies($limit: Int) {
              listMovies(limit: $limit) {
                items {
                  id
                }
              }
            }
          `,
          variables: { limit: 10000 }
        }),
        client.graphql({
          query: `
            query ListShows($limit: Int) {
              listShows(limit: $limit) {
                items {
                  id
                }
              }
            }
          `,
          variables: { limit: 10000 }
        })
      ]);

      const movies = movieStats.data.listMovies.items || [];
      const shows = showStats.data.listShows.items || [];
      const totalContent = movies.length + shows.length;

      console.log(`Starting bulk recalculation for ${totalContent} items (${movies.length} movies, ${shows.length} shows)`);

      let processed = 0;
      let successful = 0;
      let failed = 0;

      // Process movies
      for (const movie of movies) {
        try {
          await client.graphql({
            query: `
              mutation CalculateHypeRating($contentID: ID!, $contentType: String!) {
                calculateHypeRating(contentID: $contentID, contentType: $contentType) {
                  contentID
                  hypeRating
                }
              }
            `,
            variables: {
              contentID: movie.id,
              contentType: 'MOVIE'
            }
          });
          successful++;
        } catch (error) {
          console.error(`Failed to recalculate movie ${movie.id}:`, error);
          failed++;
        }
        processed++;
        
        // Add small delay to avoid overwhelming the system
        if (processed % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Process shows
      for (const show of shows) {
        try {
          await client.graphql({
            query: `
              mutation CalculateHypeRating($contentID: ID!, $contentType: String!) {
                calculateHypeRating(contentID: $contentID, contentType: $contentType) {
                  contentID
                  hypeRating
                }
              }
            `,
            variables: {
              contentID: show.id,
              contentType: 'SHOW'
            }
          });
          successful++;
        } catch (error) {
          console.error(`Failed to recalculate show ${show.id}:`, error);
          failed++;
        }
        processed++;
        
        // Add small delay to avoid overwhelming the system
        if (processed % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      return {
        totalContent,
        processed,
        successful,
        failed,
        movies: movies.length,
        shows: shows.length
      };
    } catch (error) {
      console.error('Error in bulk recalculation:', error);
      throw error;
    }
  },

  // Get HypeRating analytics from DynamoDB only
  async getHypeRatingAnalytics() {
    try {
      const [movieStats, showStats] = await Promise.all([
        client.graphql({
          query: `
            query ListMovies($limit: Int) {
              listMovies(limit: $limit) {
                items {
                  id
                  hypeRating
                  hypeScore
                  totalInteractions
                }
              }
            }
          `,
          variables: { limit: 1000 }
        }),
        client.graphql({
          query: `
            query ListShows($limit: Int) {
              listShows(limit: $limit) {
                items {
                  id
                  hypeRating
                  hypeScore
                  totalInteractions
                }
              }
            }
          `,
          variables: { limit: 1000 }
        })
      ]);

      const allContent = [
        ...(movieStats.data.listMovies.items || []),
        ...(showStats.data.listShows.items || [])
      ].filter(item => item.hypeRating !== undefined); // Only include items with HypeRating data

      const analytics = {
        totalContent: allContent.length,
        averageHypeRating: allContent.length > 0 ? allContent.reduce((sum, item) => sum + (item.hypeRating || 0), 0) / allContent.length : 0,
        highHypeContent: allContent.filter(item => (item.hypeRating || 0) >= 7).length,
        mediumHypeContent: allContent.filter(item => (item.hypeRating || 0) >= 4 && (item.hypeRating || 0) < 7).length,
        lowHypeContent: allContent.filter(item => (item.hypeRating || 0) < 4).length,
        totalInteractions: allContent.reduce((sum, item) => sum + (item.totalInteractions || 0), 0)
      };

      return analytics;
    } catch (error) {
      console.error('Error getting HypeRating analytics:', error);
      throw error;
    }
  }
};